/**
 * The Library's storage footer (W2-10 projects-data; DESIGN.md §4.1, §5.13; ARCHITECTURE.md §10): space used of
 * the browser's quota, whether the browser may clear Phiedit's data ("Protect my charts" asks for persistent
 * storage), low space and failed saves, the Trash with its count, and "Download all as backup" (every chart as
 * .pez + phiedit.json, zipped by the io worker, with progress). Every state pairs an icon with words.
 */

import { useState } from 'react';
import type { AppServices } from '../../app/registry/types';
import { Button, Icon, ProgressBar } from '../../ui';
import { useIoProgress, useStorageHealth, useText } from './hooks';
import { m } from './library.messages';
import { backupAll, protectCharts } from './libraryActions';
import { formatBytes } from './libraryModel';
import s from './Library.module.css';

/** Less free space than this is "low" (DESIGN.md §5.13). */
export const LOW_SPACE_BYTES = 200 * 1024 * 1024;

export interface StorageFooterProps {
  readonly services: AppServices;
  /** Charts in the Trash (link "Trash (N)"). */
  readonly trashCount: number;
  /** The page the footer is on: the Trash does not link to itself (UX-089). */
  readonly page?: 'home' | 'trash';
  /** Live charts (the backup is offered when there is one). */
  readonly liveCount: number;
}

export function StorageFooter(props: StorageFooterProps) {
  const { services, trashCount, liveCount } = props;
  const text = useText(services);
  const { health, failed, refresh } = useStorageHealth(services);
  const backupProgress = useIoProgress(services, 'backup');
  const [backingUp, setBackingUp] = useState(false);
  const [protecting, setProtecting] = useState(false);

  const used = health?.usageBytes ?? 0;
  const quota = health?.quotaBytes ?? 0;
  const free = quota > 0 ? quota - used : Infinity;
  const low = health !== null && free < LOW_SPACE_BYTES;

  const runBackup = async (): Promise<void> => {
    setBackingUp(true);
    try {
      await backupAll(services);
    } finally {
      setBackingUp(false);
    }
  };

  const protect = async (): Promise<void> => {
    setProtecting(true);
    try {
      await protectCharts(services);
    } finally {
      setProtecting(false);
      refresh();
    }
  };

  return (
    <footer className={s.footer} aria-label={text.t(m.storageLabel)}>
      <div className={s.footerRow}>
        <span className={s.footerItem}>
          <Icon name="HardDrive" size={16} />
          <span>
            {health === null
              ? text.t(failed ? m.storageUnknown : m.storageLoading)
              : quota > 0
                ? text.t(m.usage, { used: formatBytes(used, text), quota: formatBytes(quota, text) })
                : text.t(m.usageUnknown, { used: formatBytes(used, text) })}
          </span>
        </span>
        {health !== null && quota > 0 && (
          <span className={s.footerMeter}>
            <ProgressBar value={Math.min(1, used / quota)} label={{ key: m.storageLabel }} />
          </span>
        )}
        {health !== null &&
          (health.persisted ? (
            <span className={s.footerItem} data-tone="ok">
              <Icon name="ShieldCheck" size={16} />
              <span>{text.t(m.protectedLine)}</span>
            </span>
          ) : (
            <span className={s.footerItem} data-tone="warning">
              <Icon name="ShieldAlert" size={16} />
              <span>{text.t(m.notProtected)}</span>
              <Button
                size="sm"
                icon="Lock"
                disabledReason={protecting ? { key: m.busy } : null}
                onClick={() => {
                  void protect();
                }}
              >
                {text.t(m.protect)}
              </Button>
            </span>
          ))}
        {props.page !== 'trash' && (
          <a className={s.footerLink} href="#/trash">
            <Icon name="Trash2" size={16} />
            <span>{text.t(m.trashLink, { count: trashCount })}</span>
          </a>
        )}
      </div>
      {health !== null && (low || health.lastWriteError !== null || !health.opfs) && (
        <ul className={s.footerNotes}>
          {low && (
            <li data-tone="warning">
              <Icon name="TriangleAlert" size={16} />
              <span>{text.t(m.lowSpace)}</span>
            </li>
          )}
          {health.lastWriteError !== null && (
            <li data-tone="error" role="alert">
              <Icon name="CircleAlert" size={16} />
              <span>{text.t(m.writeError, { reason: health.lastWriteError })}</span>
            </li>
          )}
          {!health.opfs && (
            <li data-tone="muted">
              <Icon name="Info" size={16} />
              <span>{text.t(m.opfsOff)}</span>
            </li>
          )}
        </ul>
      )}
      <div className={s.footerRow}>
        <Button
          icon="HardDriveDownload"
          variant={!health?.persisted || low ? 'primary' : 'secondary'}
          disabledReason={backingUp ? { key: m.backupProgress } : liveCount === 0 ? { key: m.backupNothing } : null}
          onClick={() => {
            void runBackup();
          }}
        >
          {text.t(m.backup)}
        </Button>
        {(backingUp || backupProgress !== null) && (
          <span className={s.footerMeter}>
            <ProgressBar
              value={backupProgress === null ? null : backupProgress.p}
              label={{ key: m.backupProgress }}
            />
          </span>
        )}
      </div>
    </footer>
  );
}
